import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Hourglass, ArrowRight } from "lucide-react";
import { scrollToId } from "./Header";
import { useLang } from "@/i18n/LanguageContext";

const TARGET = new Date("2030-01-01T00:00:00+05:30").getTime();
const pad = (n) => String(n).padStart(2, "0");

const Unit = ({ v, label, testId }) => (
  <div className="flex-1 text-center rounded-xl bg-white/10 backdrop-blur-sm border border-white/25 py-3.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.15)]">
    <motion.p
      key={v}
      initial={{ y: -10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      data-testid={testId}
      className="font-mono text-2xl sm:text-3xl font-extrabold text-white tabular-nums leading-none drop-shadow-[0_2px_6px_rgba(0,0,0,0.35)]"
    >
      {pad(v)}
    </motion.p>
    <p className="text-[9px] font-mono font-bold uppercase tracking-[0.2em] text-blue-200 mt-1.5">{label}</p>
  </div>
);

export const Countdown = () => {
  const [now, setNow] = useState(() => Date.now());
  const { t } = useLang();

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const diff = Math.max(0, TARGET - now);
  const years = Math.floor(diff / 31557600000);
  const days = Math.floor((diff % 31557600000) / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);

  return (
    <motion.div
      data-testid="hero-countdown"
      initial={{ opacity: 0, y: 30, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.9, delay: 1.25, ease: [0.22, 1, 0.36, 1] }}
      className="relative rounded-3xl border border-blue-400/40 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900 p-6 sm:p-7 shadow-[0_30px_70px_-20px_rgba(29,78,216,0.65)] overflow-hidden"
    >
      <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-cyan-400/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-16 w-56 h-56 rounded-full bg-indigo-500/25 blur-3xl pointer-events-none" />

      <div className="relative">
        <span className="inline-flex items-center gap-2 text-[10px] font-mono font-bold uppercase tracking-[0.2em] text-blue-800 bg-white px-3.5 py-1.5 rounded-full shadow-md">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 live-dot" />
          {t("cd.badge")}
        </span>
      </div>

      <div className="relative flex items-center gap-2.5 mt-4">
        <Hourglass className="w-5 h-5 text-amber-300" />
        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-blue-100">
          {t("cd.title")}
        </p>
      </div>

      <div className="relative mt-5 flex gap-2.5">
        <Unit v={years} label={t("cd.years")} testId="countdown-years" />
        <Unit v={days} label={t("cd.days")} testId="countdown-days" />
      </div>
      <div className="relative mt-2.5 flex gap-2.5">
        <Unit v={hours} label={t("cd.hours")} testId="countdown-hours" />
        <Unit v={mins} label={t("cd.mins")} testId="countdown-mins" />
        <Unit v={secs} label={t("cd.secs")} testId="countdown-secs" />
      </div>

      <p className="relative mt-5 text-xs sm:text-[13px] text-blue-100 leading-relaxed">
        {t("cd.bodyA")}
        <span className="font-bold text-white">{t("cd.bodyB")}</span>
        {t("cd.bodyC")}
        <span className="font-semibold text-amber-300">{t("cd.bodyD")}</span>
      </p>

      <button
        data-testid="countdown-roadmap-link"
        onClick={() => scrollToId("#roadmap")}
        className="group relative mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-amber-300 hover:text-white transition-colors"
      >
        {t("cd.link")}
        <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </motion.div>
  );
};
